import React, { useState, useEffect } from 'react';
import { machineryService } from '../../services/machineryService';
import { getCurrentTimeLima } from '../../utils/dateUtils';
import { formatBitacoraReading, sanitizeBitacoraReadingInput } from '../../utils/bitacoraFormat';

/**
 * Modal para registrar un evento de bitácora (horómetro/kilo) desde documentos u otros contextos.
 * Se usa cuando el producto tiene "registrar avance" habilitado.
 * Si se pasan sourceEventDate (y opcionalmente sourceEventTime), no se muestran los campos de fecha/hora y se usa la fecha de la guía/documento.
 * Si collectOnly=true, al enviar no se llama al backend sino que se pasa el objeto del evento a onSuccess(eventData) para que el padre lo envíe después con el documento.
 */
const MachineryEventModal = ({ isOpen, onClose, onSuccess, productName = '', sourceEventDate = null, sourceEventTime = null, collectOnly = false, initialFormData = null, sourceProjectId = null }) => {
  const useSourceDateTime = Boolean(sourceEventDate);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [eventTypesList, setEventTypesList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    event_date: new Date().toISOString().split('T')[0],
    event_time: getCurrentTimeLima(),
    record_type: 'vehicle',
    machine_id: '',
    vehicle_id: '',
    hours: '',
    kilometers: '',
    operator: '',
    event_type: '',
    project_id: '',
    notes: ''
  });

  useEffect(() => {
    if (!isOpen) return;
    setErrorMessage('');
    const date = useSourceDateTime && sourceEventDate
      ? (sourceEventDate.includes('T') ? sourceEventDate.split('T')[0] : sourceEventDate)
      : new Date().toISOString().split('T')[0];
    const time = useSourceDateTime ? (sourceEventTime != null ? sourceEventTime : getCurrentTimeLima()) : getCurrentTimeLima();
    const defaults = {
      event_date: date,
      event_time: time,
      record_type: 'vehicle',
      machine_id: '',
      vehicle_id: '',
      hours: '',
      kilometers: '',
      operator: '',
      event_type: '',
      project_id: '',
      notes: ''
    };
    if (initialFormData && typeof initialFormData === 'object') {
      setFormData({
        ...defaults,
        ...initialFormData,
        machine_id: initialFormData.machine_id != null ? String(initialFormData.machine_id) : '',
        vehicle_id: initialFormData.vehicle_id != null ? String(initialFormData.vehicle_id) : '',
        project_id: initialFormData.project_id != null ? String(initialFormData.project_id) : ''
      });
    } else {
      setFormData(defaults);
    }
    loadData();
  }, [isOpen, useSourceDateTime, sourceEventDate, sourceEventTime]);

  const loadData = async () => {
    try {
      // Este modal (desde documento) usa solo tipos de evento monetarios
      const res = await machineryService.getMachineryFormData({ event_types_category: 'monetario' });
      if (res.success && res.data) {
        setMachines(res.data.machines || []);
        setVehicles(res.data.vehicles || []);
        setConductors(res.data.conductors || []);
        setEventTypesList(Array.isArray(res.data.event_types) ? res.data.event_types : []);
        setProjects(Array.isArray(res.data.projects) ? res.data.projects : []);
      } else {
        setErrorMessage(res.message || 'Error al cargar datos de maquinaria');
      }
    } catch (err) {
      setErrorMessage('Error al cargar datos de maquinaria');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = (name === 'hours' || name === 'kilometers')
      ? sanitizeBitacoraReadingInput(value)
      : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'record_type') {
        if (value === 'machine') {
          next.vehicle_id = '';
        } else {
          next.machine_id = '';
        }
        next.hours = '';
        next.kilometers = '';
      }
      return next;
    });
  };

  const isMachine = formData.record_type === 'machine';
  const selectedMachine = isMachine ? machines.find(m => String(m.id) === String(formData.machine_id)) : null;
  const selectedVehicle = !isMachine ? vehicles.find(v => String(v.id) === String(formData.vehicle_id)) : null;
  const configSource = isMachine ? selectedMachine : selectedVehicle;
  const allowHour = configSource && typeof configSource.control_hour_meter === 'boolean' ? configSource.control_hour_meter : true;
  const allowKm = configSource && typeof configSource.control_mileage === 'boolean' ? configSource.control_mileage : true;
  const lastHours = isMachine ? selectedMachine?.last_hours : selectedVehicle?.last_hours;
  const lastKilometers = isMachine ? selectedMachine?.last_kilometers : selectedVehicle?.last_kilometers;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (configSource && !allowHour && !allowKm) {
      setErrorMessage('Este equipo no tiene configurado control de horómetro ni kilometraje.');
      return;
    }
    const hasHours = allowHour && formData.hours !== '' && formData.hours != null;
    const hasKm = allowKm && formData.kilometers !== '' && formData.kilometers != null;
    const hasMachine = isMachine && formData.machine_id && (hasHours || hasKm);
    const hasVehicle = !isMachine && formData.vehicle_id && (hasHours || hasKm);
    if (!hasMachine && !hasVehicle) {
      setErrorMessage(isMachine ? 'Seleccione una máquina e ingrese horómetro y/o kilometraje' : 'Seleccione un vehículo e ingrese horómetro y/o kilometraje');
      return;
    }
    const projectIdForPayload = (collectOnly && (sourceProjectId != null && sourceProjectId !== ''))
      ? sourceProjectId
      : (formData.project_id && formData.project_id !== '' ? formData.project_id : null);
    const data = {
      event_date: formData.event_date,
      event_time: formData.event_time || null,
      record_type: formData.record_type || 'vehicle',
      machine_id: isMachine ? (formData.machine_id || null) : null,
      vehicle_id: !isMachine ? (formData.vehicle_id || null) : null,
      hours: allowHour && formData.hours !== '' ? formData.hours : null,
      kilometers: allowKm && formData.kilometers !== '' ? formData.kilometers : null,
      operator: formData.operator || null,
      event_type: formData.event_type || null,
      project_id: projectIdForPayload != null && projectIdForPayload !== '' ? projectIdForPayload : null,
      notes: formData.notes || null
    };
    if (collectOnly) {
      const parts = [];
      if (isMachine && selectedMachine) parts.push(`Máquina: ${selectedMachine.code} - ${selectedMachine.name}`);
      else if (!isMachine && selectedVehicle) parts.push(`Vehículo: ${selectedVehicle.code} - ${selectedVehicle.name}`);
      if (formData.event_time) parts.push(formData.event_time.substring(0, 5));
      if (allowHour && formData.hours !== '') parts.push(`${formatBitacoraReading(formData.hours)} h`);
      if (allowKm && formData.kilometers !== '') parts.push(`${formatBitacoraReading(formData.kilometers)} km`);
      if (formData.operator) parts.push(formData.operator);
      if (formData.event_type) parts.push(formData.event_type);
      const selectedProject = projects.find(p => String(p.id) === String(formData.project_id));
      if (selectedProject && selectedProject.name) parts.push(selectedProject.name);
      data.displaySummary = parts.length ? parts.join(' · ') : 'Evento registrado';
      onSuccess && onSuccess(data);
      onClose();
      return;
    }
    setLoading(true);
    try {
      const response = await machineryService.createEvent(data);
      if (response.success) {
        onSuccess && onSuccess();
        onClose();
      } else {
        setErrorMessage(response.message || 'Error al registrar el evento');
      }
    } catch (err) {
      setErrorMessage('Error al registrar el evento');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Registrar evento de seguimiento
            {productName ? ` — ${productName}` : ''}
          </h3>
          <p className="text-sm text-gray-500 mt-1">Registre horómetro y/o kilometraje del vehículo o máquina.</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errorMessage && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{errorMessage}</div>
          )}
          {/* Fila 1: Tipo de registro, Tipo de evento, Vehículo/Máquina */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de registro *</label>
              <select
                name="record_type"
                value={formData.record_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="machine">Maquinaria</option>
                <option value="vehicle">Vehículo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de evento</label>
              <select
                name="event_type"
                value={formData.event_type || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccionar tipo</option>
                {eventTypesList.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            {isMachine ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Máquina *</label>
                <select
                  name="machine_id"
                  value={formData.machine_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Seleccionar</option>
                  {machines.map(m => (
                    <option key={m.id} value={m.id}>{m.code} - {m.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo *</label>
                <select
                  name="vehicle_id"
                  value={formData.vehicle_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Seleccionar</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.code} - {v.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {/* Fila 2: Fecha (si aplica), Hora, Horómetro, Kilometraje */}
          {!useSourceDateTime && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                <input
                  type="date"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                <input
                  type="time"
                  name="event_time"
                  value={formData.event_time || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {useSourceDateTime && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                <input
                  type="time"
                  name="event_time"
                  value={formData.event_time || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            )}
            {allowHour && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Horómetro (horas)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="hours"
                  value={formData.hours}
                  onChange={handleChange}
                  placeholder="Ej. 2450.50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {lastHours != null && (
                  <p className="mt-1 text-xs text-gray-500">
                    Último horómetro: {formatBitacoraReading(lastHours)} h
                  </p>
                )}
              </div>
            )}
            {allowKm && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje (km)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="kilometers"
                  value={formData.kilometers}
                  onChange={handleChange}
                  placeholder="Ej. 125000.25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {lastKilometers != null && (
                  <p className="mt-1 text-xs text-gray-500">
                    Último kilometraje: {formatBitacoraReading(lastKilometers)} km
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Operador / Conductor</label>
            <select
              name="operator"
              value={formData.operator}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Seleccionar</option>
              {conductors.map(c => (
                <option key={c.id} value={c.name}>{c.name}{c.license ? ` (${c.license})` : ''}</option>
              ))}
            </select>
          </div>
          {!collectOnly && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto</label>
              <select
                name="project_id"
                value={formData.project_id || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccionar</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Observaciones"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg"
            >
              {loading ? 'Guardando...' : 'Registrar evento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MachineryEventModal;
